import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Progress,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import InlineErrorState from '../../../components/states/InlineErrorState';
import InlineScopePrompt from '../../_shared/InlineScopePrompt';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import { isUnreadable } from '../../analytics/utils/statValue';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateCourtRecording } from '../hooks/useCreateCourtRecording';
import { translate } from '../../../i18n';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_FILE_SIZE_MB = 200;
const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime'];

export default function UploadRecordingModal({ isOpen, onClose }: Props) {
  const [selectedCourtId, setSelectedCourtId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const { selectedOrganization } = useSelectedOrganization();
  const selectedLocation = useSelectedLocation.use.selectedLocation();
  const { data: courts, isError: isCourtsError, state: courtsReadState } = useGetCourts(selectedLocation?.id || '');

  const createMutation = useCreateCourtRecording(
    selectedOrganization?.id || '',
    () => {
      toast({ title: translate('court_recording.recording_uploaded'), description: translate('court_recording.analysis_will_begin'), status: 'success', duration: 3000 });
      handleClose();
    },
  );

  const handleClose = useCallback(() => {
    setSelectedCourtId(null);
    setFile(null);
    onClose();
  }, [onClose]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ACCEPTED_TYPES.includes(selected.type)) {
      toast({ title: translate('court_recording.invalid_file_type'), description: translate('court_recording.invalid_file_type_desc'), status: 'error' });
      return;
    }
    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast({ title: translate('court_recording.file_too_large'), description: `${translate('court_recording.file_too_large_desc').replace('{size}', String(MAX_FILE_SIZE_MB))}`, status: 'error' });
      return;
    }
    setFile(selected);
  }, [toast]);

  const handleUpload = useCallback(() => {
    if (!file || !selectedCourtId) return;
    createMutation.mutate({ file, courtId: selectedCourtId });
  }, [file, selectedCourtId, createMutation]);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('court_recording.upload_title')}
      onSubmit={handleUpload}
      submitLabel={translate('court_recording.upload')}
      cancelLabel={translate('court_recording.cancel')}
      isSubmitting={createMutation.isLoading}
      isSubmitDisabled={!file || !selectedCourtId}
    >
      <Box mb={4}>
        {/*
          `(courts || [])` rendered an empty court select on a failed read, and
          the upload cannot proceed without a court — so the operator saw a
          venue with no courts rather than a read that did not answer
          (R89-4 / D57).
        */}
        {isUnreadable(isCourtsError, courts) ? (
          <InlineErrorState message={translate('courts.error_loading')} />
        ) : courtsReadState === 'never-issued' ? (
          /*
            Never ISSUED: no club is selected. Before run 134 the read went out
            as `courts?locationId=` and came back 200 `[]`, so the operator was
            shown an empty court Select on a modal whose upload cannot proceed
            without a court — a dead end with no stated reason (R110-6).
          */
          <InlineScopePrompt gate="location" />
        ) : (
        <FormField
          type="select"
          label={translate('court_recording.court')}
          value={(selectedCourtId ?? '').toString()}
          onChange={(val) => setSelectedCourtId(val ? Number(val) : null)}
          options={(courts || []).map((court: CourtDto) => ({ value: court.id.toString(), label: court.name }))}
          placeholder={translate('court_recording.select_court')}
          isRequired
        />
        )}
      </Box>

      <FormControl isRequired>
        <FormLabel color={textColor}>{translate('court_recording.video_file')}</FormLabel>
        <Input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.mov"
          onChange={handleFileChange}
          p={1}
        />
        {file && (
          <Text fontSize="sm" color="secondaryGray.600" mt={1}>
            {file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)
          </Text>
        )}
      </FormControl>

      {createMutation.isLoading && (
        <Box mt={4}>
          <Text fontSize="sm" color="secondaryGray.600" mb={1}>{translate('court_recording.uploading')}</Text>
          <Progress size="sm" isIndeterminate colorScheme="brand" borderRadius="4px" />
        </Box>
      )}
    </FormModal>
  );
}
